import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageParts";
import { DevNav, StatusPill } from "@/components/developer/DevNav";

const TYPES = { personal: "Personal script (your own account only)", server: "Server app (confidential client, other users via OAuth)", browser: "Browser / mobile app (public client, PKCE, no secret)" };
const EMPTY = { name: "", description: "", app_type: "personal", website_url: "", intended_use: "", contact_email: "", scopes: [] };

export function ScopePicker({ all, risky, value, onChange, appType, disabled }) {
  return (
    <fieldset className="grid gap-1 sm:grid-cols-2" disabled={disabled} data-testid="scope-picker">
      <legend className="mb-2 text-xs text-slate-400">Scopes {appType !== "personal" && "· high-risk scopes need staff review before other users can grant them"}</legend>
      {Object.entries(all).map(([s, d]) => (
        <label key={s} className="flex items-start gap-2 text-xs text-slate-300" title={d}>
          <input type="checkbox" checked={value.includes(s)} onChange={(e) => onChange(e.target.checked ? [...value, s] : value.filter((x) => x !== s))} data-testid={`scope-${s}`} />
          <span className="font-mono">{s}{risky.includes(s) && appType !== "personal" && <span className="ml-1 text-copper-300">· review</span>}</span>
        </label>
      ))}
    </fieldset>
  );
}

function RegisterForm({ meta, onDone }) {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, website_url: f.website_url || null, contact_email: f.contact_email || null };
      await api.post("/developer/apps", body);
      toast.success("Application registered as a draft");
      setF(EMPTY); onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form className="panel grid gap-3 p-6" onSubmit={submit} data-testid="dev-register-form">
      <p className="eyebrow">Register an application</p>
      <input className="field" aria-label="Application name" placeholder="Application name" value={f.name} onChange={set("name")} data-testid="dev-app-name-input" />
      <textarea className="field" rows={2} aria-label="Description" placeholder="Description" value={f.description} onChange={set("description")} data-testid="dev-app-description-input" />
      <select className="field" aria-label="Application type" value={f.app_type} onChange={set("app_type")} data-testid="dev-app-type-select">
        {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      <input className="field" aria-label="Website URL" placeholder="Website URL (optional)" value={f.website_url} onChange={set("website_url")} data-testid="dev-app-website-input" />
      <textarea className="field" rows={2} aria-label="Intended use" placeholder="Intended use: what will the app do with Planar Foundry data?" value={f.intended_use} onChange={set("intended_use")} data-testid="dev-app-use-input" />
      <input className="field" aria-label="Contact email" placeholder="Contact email" value={f.contact_email} onChange={set("contact_email")} data-testid="dev-app-contact-input" />
      <ScopePicker all={meta.scopes} risky={meta.high_risk_scopes} value={f.scopes} appType={f.app_type} onChange={(scopes) => setF({ ...f, scopes })} />
      <button className="btn-gold justify-self-start" disabled={f.name.length < 3} data-testid="dev-register-submit">Register (draft)</button>
    </form>
  );
}

function Offers({ offers, onDone }) {
  if (!offers.length) return null;
  const act = (id, what) => api.post(`/developer/apps/${id}/transfer/${what}`).then(() => { toast.success(what === "accept" ? "You now own this application" : "Offer declined"); onDone(); }).catch((e) => e && toast.error(formatApiError(e)));
  return offers.map((o) => (
    <div key={o.app_id} className="panel mb-4 flex flex-wrap items-center gap-3 border-amber-600/50 p-4" data-testid={`dev-transfer-offer-${o.app_id}`}>
      <span className="text-sm text-amber-100">You were offered ownership of <b>{o.name}</b>. Its API keys will be revoked on acceptance.</span>
      <button className="btn-gold text-xs" onClick={() => act(o.app_id, "accept")} data-testid={`dev-transfer-accept-${o.app_id}`}>Accept</button>
      <button className="btn-outline text-xs" onClick={() => act(o.app_id, "cancel")} data-testid={`dev-transfer-decline-${o.app_id}`}>Decline</button>
    </div>
  ));
}

export default function DevApps() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["dev-apps"], enabled: !!user, queryFn: () => api.get("/developer/apps").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["dev-apps"] });
  return (
    <div className="container-x py-12" data-testid="dev-apps-page">
      <DevNav />
      <PageHeader eyebrow="Developer portal" title="Your applications" testid="dev-apps-header" description="Register apps, issue credentials and connect other members through OAuth." />
      {!user ? <p className="text-slate-400"><Link to="/login" state={{ from: "/developers/apps" }} className="text-amber-300 underline">Sign in</Link> to manage applications.</p> : data && (
        <div className="grid gap-8 lg:grid-cols-[1fr_440px]">
          <div>
            <Offers offers={data.transfer_offers} onDone={refresh} />
            <ul className="grid gap-3">
              {data.items.map((a) => (
                <li key={a.app_id}><Link to={`/developers/apps/${a.app_id}`} className="panel block p-5 transition-colors hover:border-amber-500/60" data-testid={`dev-app-card-${a.app_id}`}>
                  <div className="flex flex-wrap items-center gap-2"><h3 className="font-heading text-lg text-amber-100">{a.name}</h3><StatusPill status={a.status} testid={`dev-app-status-${a.app_id}`} />
                    <span className="font-mono text-[10px] uppercase text-slate-500">{a.app_type} · {a.my_role}</span>
                    {a.review?.status === "pending" && <span className="font-mono text-[10px] uppercase text-copper-300">scope review pending</span>}</div>
                  <p className="mt-1 line-clamp-2 text-sm text-slate-400">{a.description}</p>
                  <p className="mt-2 font-mono text-[10px] text-slate-500">{a.credentials.filter((c) => !c.revoked_at).length} active credentials · {a.connected_users} connected users · last used {a.last_used_at?.slice(0, 16) || "never"}</p>
                </Link></li>
              ))}
            </ul>
            {!data.items.length && <p className="text-sm text-slate-500" data-testid="dev-apps-empty">No applications yet.</p>}
          </div>
          <RegisterForm meta={data} onDone={refresh} />
        </div>
      )}
    </div>
  );
}
